import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Database, Play, RefreshCw, TableProperties, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { demoSteps } from '../data/mockData';
import TypewriterEffect from '../components/TypewriterEffect';
import InteractiveDiagram from '../components/InteractiveDiagram';
import DemoTimeline from '../components/DemoTimeline';

const languageSteps = [
  'Open a database connection',
  'Write SQL manually',
  'Send parameters safely',
  'Read rows from the result',
  'Map every column into objects'
];

const frameworkSteps = [
  'Create entity classes',
  'Write LINQ with DbContext',
  'EF Core maps and saves'
];

const HomePage = () => {
  const [languageProgress, setLanguageProgress] = useState(-1);
  const [frameworkProgress, setFrameworkProgress] = useState(-1);
  const [juiceAnimating, setJuiceAnimating] = useState(false);
  const [juiceFinished, setJuiceFinished] = useState(false);

  useEffect(() => {
    if (!juiceAnimating) return undefined;

    const timers = [
      ...languageSteps.map((_, index) =>
        setTimeout(() => setLanguageProgress(index), index * 650)
      ),
      ...frameworkSteps.map((_, index) =>
        setTimeout(() => setFrameworkProgress(index), index * 360)
      ),
      setTimeout(() => {
        setJuiceAnimating(false);
        setJuiceFinished(true);
      }, languageSteps.length * 650)
    ];

    return () => timers.forEach(clearTimeout);
  }, [juiceAnimating]);

  const runJuiceDemo = () => {
    setLanguageProgress(-1);
    setFrameworkProgress(-1);
    setJuiceFinished(false);
    setJuiceAnimating(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="gradient-animate absolute inset-0" />
        <div className="container-custom relative py-16 sm:py-24 lg:py-32">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-4" variant="secondary">
              Interactive Learning Platform
            </Badge>
            <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Explore EF Core
            </h1>
            <div className="mb-6 text-xl text-muted-foreground sm:text-2xl">
              <TypewriterEffect 
                texts={[
                  'Code-First Development',
                  'LINQ Queries',
                  'Migrations',
                  'CRUD Patterns'
                ]}
              />
            </div>
            <p className="mb-8 text-base leading-relaxed text-muted-foreground sm:text-lg">
              A demo-class companion for explaining Entity Framework Core through visual flows and hands-on operations.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/playground">
                <Button size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90">
                  Open Playground
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/playground">
                <Button size="lg" variant="outline">
                  Try Mini Demos
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Language vs Framework */}
      <section className="py-16 sm:py-24">
        <div className="container-custom">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4" variant="secondary">
              Beginner Mode
            </Badge>
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Language vs Framework
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-muted-foreground">
              C# is the language you write. EF Core is a framework that helps your C# application talk to a database without repeating the same low-level data access code again and again.
            </p>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-lg border border-warning/30 bg-card p-6"
            >
              <div className="grid gap-5 sm:grid-cols-[150px_1fr] sm:items-center">
                <div className="grid place-items-center rounded-lg border border-border bg-warning/10 p-5">
                  <motion.div
                    animate={{ rotate: juiceAnimating ? [0, -8, 8, 0] : 0 }}
                    transition={{ duration: 0.5, repeat: juiceAnimating ? Infinity : 0, repeatDelay: 0.4 }}
                    className="text-7xl"
                    aria-hidden="true"
                  >
                    🍊
                  </motion.div>
                </div>
                <div>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-semibold">C# Language</h3>
                    <Badge variant="outline">Fresh orange</Badge>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    A language gives you raw power. You can make anything, but you handle more details yourself, just like peeling, cutting, squeezing, filtering, and serving the orange.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative overflow-hidden rounded-lg border border-primary/30 bg-card p-6"
            >
              <div className="grid gap-5 sm:grid-cols-[150px_1fr] sm:items-center">
                <div className="grid place-items-center rounded-lg border border-border bg-primary/10 p-5">
                  <motion.div
                    animate={{ y: juiceAnimating && !juiceFinished ? [0, -5, 0] : 0 }}
                    transition={{ duration: 0.6, repeat: juiceAnimating && !juiceFinished ? Infinity : 0 }}
                    className="text-7xl"
                    aria-hidden="true"
                  >
                    🧃
                  </motion.div>
                </div>
                <div>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-semibold">EF Core Framework</h3>
                    <Badge variant="outline">Ready mix</Badge>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    A framework gives you prepared structure. EF Core still uses C#, but it handles common database work like mapping, tracking, querying, and saving faster.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="mt-6 rounded-lg border border-border bg-surface p-5 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold">Why EF Core Helps</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Click through the same database task: making juice manually vs using prepared structure.
                </p>
              </div>
              <Button onClick={runJuiceDemo} disabled={juiceAnimating}>
                <Play className="mr-2 h-4 w-4" />
                {juiceAnimating ? 'Making Juice...' : juiceFinished ? 'Make Again' : 'Make Juice'}
              </Button>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch">
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="mb-4 flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-warning" />
                  <h4 className="font-semibold">Without EF Core</h4>
                </div>
                <div className="space-y-2">
                  {languageSteps.map((step, index) => (
                    <div
                      key={step}
                      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                        languageProgress >= index
                          ? 'border-warning/40 bg-warning/10 text-foreground'
                          : 'border-border bg-muted/20 text-muted-foreground'
                      }`}
                    >
                      <span>{step}</span>
                      {languageProgress >= index && <CheckCircle2 className="h-4 w-4 text-warning" />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden items-center justify-center lg:flex">
                <ArrowRight className="h-8 w-8 text-primary" />
              </div>

              <div className="rounded-lg border border-border bg-card p-4">
                <div className="mb-4 flex items-center gap-2">
                  <Database className="h-5 w-5 text-success" />
                  <h4 className="font-semibold">With EF Core</h4>
                </div>
                <div className="space-y-2">
                  {frameworkSteps.map((step, index) => (
                    <div
                      key={step}
                      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                        frameworkProgress >= index
                          ? 'border-success/40 bg-success/10 text-foreground'
                          : 'border-border bg-muted/20 text-muted-foreground'
                      }`}
                    >
                      <span>{step}</span>
                      {frameworkProgress >= index && <CheckCircle2 className="h-4 w-4 text-success" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: RefreshCw, text: 'Less repeated CRUD code' },
                { icon: TableProperties, text: 'C# classes map to tables' },
                { icon: Database, text: 'LINQ becomes SQL' }
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div key={item.text} className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3 text-sm">
                    <Icon className="h-4 w-4 text-primary" />
                    <span>{item.text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How EF Works Diagram */}
      <section className="border-y border-border bg-surface py-16 sm:py-24">
        <div className="container-custom">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              How EF Core Works
            </h2>
            <p className="text-lg text-muted-foreground">
              Click on each node to learn more about the data flow
            </p>
          </motion.div>

          <InteractiveDiagram />
        </div>
      </section>

      {/* 15-Minute Demo Plan */}
      <section className="py-16 sm:py-24">
        <div className="container-custom">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Demo Plan
            </h2>
            <p className="text-lg text-muted-foreground">
              A structured approach to demonstrating EF Core capabilities
            </p>
          </motion.div>

          <DemoTimeline steps={demoSteps} />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-secondary/5 to-background py-16 sm:py-24">
        <div className="container-custom">
          <motion.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Ready to Start Learning?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Open the playground for CRUD, LINQ, Code-First, and Database-First examples.
            </p>
            <Link to="/playground">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                Launch Playground
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
